import { THomepageCategory } from '@/types/home'
import { CategoryCard } from '../category-card'

type Props = {
  categories: THomepageCategory[]
  homeType?: 'nam' | 'nu' | 'chung'
}

/**
 * Pure CSS scroll-snap carousel — zero JavaScript initialization.
 *
 * Replaces the previous Embla-based implementation to eliminate ~50ms TBT
 * on mobile (4× CPU throttle). CSS scroll snap provides equivalent horizontal
 * browsing UX with native browser momentum scrolling.
 *
 * Deliberately no 'use client' → this is a Server Component, which means:
 * - No hydration cost
 * - No Embla bundle in the critical path
 * - Rendered as static HTML by Next.js
 */
export const CategoryCarousel: React.FC<Props> = ({ categories, homeType }) => {
  if (!categories?.length) {
    return (
      <p className='text-center text-neutral-500' role='alert'>
        No categories available.
      </p>
    )
  }

  return (
    <div
      className={[
        'flex gap-3',
        'overflow-x-auto scroll-smooth',
        'snap-x snap-mandatory',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      ].join(' ')}
      role='region'
      aria-roledescription='carousel'
      aria-label='Categories'
    >
      {categories.map((category, index) => (
        <div
          key={category.url}
          className='flex-shrink-0 snap-start basis-2/5 md:basis-1/3 lg:basis-1/4 xl:basis-1/6'
          role='listitem'
          aria-label={`Category ${index + 1} of ${categories.length}`}
        >
          <CategoryCard
            url={category.url}
            title={category.title}
            image={category.image}
            index={index + 1}
            homeType={homeType}
          />
        </div>
      ))}
    </div>
  )
}
